import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  Input,
  OnInit,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NavigationEnd, Router } from '@angular/router';
import { RouterModule } from '@angular/router';
import timelogRouteConfig from '../../../projects/Timelog/src/timelog-base/Routes';
import trainingtrackingRouteConfig from '../../../projects/TrainingTracking/src/training-tracking-base/Routes';
import { SharedFunctionsService } from 'BaseServices/sharedFunctions.service';
import { UserService } from 'BaseServices/user-service';
import { User, UserRoleType, UserRoleTypeTranslations } from 'BaseDataTypes';
import { HeaderService } from 'BaseServices/header.service';

interface ViewRole {
  name: string;
  value: UserRoleType;
}

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.css'],
  imports: [
    CommonModule,
    MatButtonModule,
    MatInputModule,
    MatSelectModule,
    MatFormFieldModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
  ],
})
export class HeaderComponent implements OnInit {
  @Input() showUserOptions: boolean = false;
  @Input() showChangeSite: boolean = false;
  @ViewChild('userContainer') userContainer: ElementRef | undefined;
  @ViewChild('changeSiteContainer') changeSiteContainer: ElementRef | undefined;
  @Input() title: string = '';

  listOfUsersSites: string[] = [];
  currentSite: string = '';
  moreThanOneRole: boolean = false;
  viewRoles: ViewRole[] = [];
  userRoleType: UserRoleType;
  constructor(
    public userService: UserService,
    public headerService: HeaderService,
    private rendere: Renderer2,
    private router: Router,
    private sharedFunctionsService: SharedFunctionsService,
  ) {
    this.userRoleType = UserRoleType.Admin;
    this.router.events.subscribe((val) => {
      if (val instanceof NavigationEnd) {
        let hrefParts = val.urlAfterRedirects.split('/');
        if (hrefParts.length > 1) {
          this.currentSite = hrefParts[1].toLowerCase();
        } else {
          this.currentSite = hrefParts[0].toLowerCase();
        }
        this.setTitleText();
      }
    });

    this.rendere.listen('window', 'click', (e: Event) => {
      if (
        this.userContainer &&
        !this.userContainer.nativeElement.contains(e.target)
      ) {
        this.showUserOptions = false;
      }
    });
    this.rendere.listen('window', 'click', (e: Event) => {
      if (
        this.changeSiteContainer &&
        !this.changeSiteContainer.nativeElement.contains(e.target)
      ) {
        this.showChangeSite = false;
      }
    });
    this.rendere.listen('window', 'click', (e: Event) => {
      if (
        this.changeSiteContainer &&
        !this.changeSiteContainer.nativeElement.contains(e.target)
      ) {
        this.showChangeSite = false;
      }
    });
  }

  ngOnInit(): void {
    this.userService.userStatusChanged$.subscribe((result) => {
      this.countRolesOfUser();
      if (result) {
        this.viewRoles = [
          {
            name: UserRoleTypeTranslations[UserRoleType.Admin],
            value: UserRoleType.Admin,
          },
          {
            name: UserRoleTypeTranslations[UserRoleType.Manager],
            value: UserRoleType.Manager,
          },
          {
            name: UserRoleTypeTranslations[UserRoleType.User],
            value: UserRoleType.User,
          },
        ];
      }
    });
  }

  /**
   * Signs current user out
   */
  signOut() {
    this.showUserOptions = false;
    this.userService.signOut();
  }

  /**
   * Gets current logged in user
   */
  get currentUser() {
    return this.userService.currentUser;
  }

  /**
   * Gets profile picture of logged in user
   */
  get profilePicture() {
    return this.userService.profilePicture;
  }

  /**
   * Shows dialog with possible sites
   */
  changeSiteMenu() {
    this.showChangeSite = !this.showChangeSite;
  }

  /**
   * Click event from change site menu options. Will navigate to new selected site.
   * @param site
   */
  changeSite(site: string) {
    this.currentSite = site.toLowerCase();
    this.router.navigate([`/${site.toLowerCase()}`]);
    this.showChangeSite = false;
    this.userService.setSelectedRoleBasedOnSite(site);
    //Reset active role
    this.userRoleType = this.userService.getSelectedRole;

    this.setTitleText();
  }

  /**
   * Gets a roleCount, which is used to determain whether the user can change site
   */
  countRolesOfUser() {
    if (this.currentUser != null) {
      this.listOfUsersSites = []; //some weird bug that runs this fuction many times when it should only run once when this is removed

      if (this.currentUser.courseRole != UserRoleType.Unauthorized) {
        this.listOfUsersSites.push('Kursus');
      }
      if (this.currentUser.timelogRole != UserRoleType.Unauthorized) {
        this.listOfUsersSites.push('Timelog');
      }
      if (this.currentUser.assetRole != UserRoleType.Unauthorized) {
        this.listOfUsersSites.push('Assets');
      }
      if (this.currentUser.equipmentRole != UserRoleType.Unauthorized) {
        this.listOfUsersSites.push('Udstyr');
      }
    }
  }

  /**
   * url decides which roletype
   * @param user
   * @returns
   */
  getCorrectRoleBasedOnUrl(user: User) {
    if (this.currentSite == 'kursus') {
      return user.courseRole;
    } else if (this.currentSite == 'timelog') {
      return user.timelogRole;
    }
    return UserRoleType;
  }

  /**
   * Changes the activeRole. Function only usable by admins.
   */
  changeActiveRole() {
    this.userService.setSelectedRole(this.userRoleType);
    let currentPage = this.router.url
      .replace(this.currentSite, '')
      .replaceAll('/', '');

    const urlNotFound = this.userService.routesAndNames.find(
      (item) => item.route === currentPage,
    );

    if (urlNotFound === undefined && this.currentSite == 'kursus') {
      this.router.navigate(['/kursus/info']);
    } else if (urlNotFound === undefined && this.currentSite == 'timelog') {
      this.router.navigate(['/timelog/rekvisitionsnumre']);
    } else if (urlNotFound === undefined && this.currentSite == 'udstyr') {
      this.router.navigate(['/udstyr/søgudstyr']);
    }
  }

  /**
   * Toggle user options dialog, when users can logout, and admins can change role
   */
  toggleShowUserOptions() {
    this.showUserOptions = !this.showUserOptions;
  }

  /**
   * Sets title text based on current url. Defaults to Kursus/ Kursusportal
   */
  setTitleText() {
    this.title = this.currentSite;
    this.title = this.title.charAt(0).toUpperCase() + this.title.slice(1);
    if (this.title == 'Kursus') {
      this.title = 'Kursusportal';
    }
  }

  get getEnumUserRoleTypes() {
    return UserRoleType;
  }
}
